// Account Activity timeline — API + types.
//
// Talks to ``GET /api/v1/accounts/{account_id}/activity`` (cursor-
// paginated). Kept separate from ``api.ts`` so the Activity tab can
// evolve without churning the rest of the Account surface.

import { api } from "@/api/client";


export type AccountActivityKind =
  | "commitment_created"
  | "commitment_status_changed"
  | "commitment_completed"
  | "commitment_cancelled"
  | "commitment_blocked"
  | "playbook_applied"
  | "file_uploaded"
  | "file_linked"
  | "touchpoint_created"
  | "touchpoint_completed"
  | "plan_year_created"
  | "plan_year_updated"
  | "account_contact_created"
  | "account_contact_updated"
  | "account_team_member_added"
  | "account_team_member_removed"
  | "account_lead_changed";


export type AccountActivitySourceKind =
  | "commitment"
  | "playbook_application"
  | "file"
  | "touchpoint"
  | "plan_year"
  | "account_contact"
  | "account_team_member";


export interface AccountActivityItem {
  id: string;
  kind: AccountActivityKind;
  title: string;
  description: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  occurred_at: string;
  source_kind: AccountActivitySourceKind | null;
  source_id: string | null;
  source_label: string | null;
  metadata: Record<string, unknown>;
}


export interface AccountActivityPage {
  items: AccountActivityItem[];
  next_cursor: string | null;
}


export interface FetchAccountActivityParams {
  accountId: string;
  cursor?: string | null;
  limit?: number;
  kinds?: AccountActivityKind[];
}


export async function fetchAccountActivity(
  params: FetchAccountActivityParams,
  opts?: { signal?: AbortSignal },
): Promise<AccountActivityPage> {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  for (const k of params.kinds ?? []) {
    sp.append("kind", k);
  }
  const qs = sp.toString();
  const path =
    `/api/v1/accounts/${encodeURIComponent(params.accountId)}/activity${
      qs ? `?${qs}` : ""
    }`;
  return api.get<AccountActivityPage>(path, opts);
}
